import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/App";
import { resolveMediaUrl } from "@/lib/media-url";
import { toast } from "sonner";
import { Heart, ChatCircle, Code, Image as ImageIcon, VideoCamera, MusicNote, Eye } from "@phosphor-icons/react";

export default function PostCard({ post, onLikeToggle }) {
  const [likeAnimating, setLikeAnimating] = useState(false);
  const [localViewsCount, setLocalViewsCount] = useState(post.views_count ?? 0);
  const cardRef = useRef(null);
  const viewTracked = useRef(false);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !viewTracked.current) {
          viewTracked.current = true;
          // Track view in backend
          apiClient.post(`/posts/${post.id}/view`)
            .then(res => {
              if (res.data?.views_count !== undefined) {
                setLocalViewsCount(res.data.views_count);
              }
            })
            .catch(() => {});
        }
      },
      { threshold: 0.5 } // trigger when 50% of the post is visible
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [post.id]);

  const handleLike = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLikeAnimating(true);
    
    try {
      const response = await apiClient.post(`/posts/${post.id}/like`);
      onLikeToggle(post.id, response.data.liked, response.data.likes_count);
    } catch (error) {
      toast.error("Failed to update like");
    }
    
    setTimeout(() => setLikeAnimating(false), 400);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {
      const hours = Math.floor(diff / (1000 * 60 * 60));
      if (hours === 0) {
        const mins = Math.floor(diff / (1000 * 60));
        return mins <= 1 ? "Just now" : `${mins}m ago`;
      }
      return `${hours}h ago`;
    }
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const hasCode = post.html_code || post.css_code || post.js_code;
  const hasMedia = post.media_urls?.length > 0;
  const hasVideo = hasMedia && post.media_urls.some(url => url.match(/\.(mp4|webm|mov)$/i));
  const hasAudio = hasMedia && post.media_urls.some(url => url.match(/\.(mp3|wav|ogg)$/i));
  const hasImage = hasMedia && post.media_urls.some(url => !url.match(/\.(mp4|webm|mov|mp3|wav|ogg)$/i));

  return (
    <article 
      ref={cardRef}
      className="bg-surface rounded-xl border border-border/40 overflow-hidden card-hover"
      data-testid={`post-card-${post.id}`}
    >
      {/* Header */}
      <div className="p-4 pb-3">
        <div className="flex items-center gap-3">
          <Link to={`/profile/${post.author_id}`} onClick={(e) => e.stopPropagation()}>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-sm font-bold text-background">
              {post.author_name?.charAt(0).toUpperCase()}
            </div>
          </Link>
          <div className="flex-1 min-w-0">
            <Link 
              to={`/profile/${post.author_id}`}
              className="font-semibold hover:text-primary transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              {post.author_name}
            </Link>
            <p className="text-xs text-muted-foreground">{formatDate(post.created_at)}</p>
          </div>
          {/* Content type badges */}
          <div className="flex items-center gap-1">
            {hasCode && (
              <div className="w-7 h-7 rounded-full bg-accent/10 flex items-center justify-center" title="Has code">
                <Code className="w-4 h-4 text-accent" />
              </div>
            )}
            {hasImage && (
              <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center" title="Has images">
                <ImageIcon className="w-4 h-4 text-primary" />
              </div>
            )}
            {hasVideo && (
              <div className="w-7 h-7 rounded-full bg-danger/10 flex items-center justify-center" title="Has video">
                <VideoCamera className="w-4 h-4 text-danger" />
              </div>
            )}
            {hasAudio && (
              <div className="w-7 h-7 rounded-full bg-warning/10 flex items-center justify-center" title="Has audio">
                <MusicNote className="w-4 h-4 text-warning" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <Link to={`/post/${post.id}`}>
        <div className="px-4 pb-3">
          <h3 className="font-heading font-semibold text-lg mb-1 hover:text-primary transition-colors">
            {post.title}
          </h3>
          <p className="text-foreground/80 line-clamp-2 text-sm leading-relaxed">
            {post.description}
          </p>
        </div>

        {/* Tags */}
        {post.tags?.length > 0 && (
          <div className="px-4 pb-3 flex flex-wrap gap-2">
            {post.tags.slice(0, 4).map(tag => (
              <span
                key={tag}
                className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs"
              >
                #{tag}
              </span>
            ))}
            {post.tags.length > 4 && (
              <span className="px-2 py-0.5 text-xs text-muted-foreground">
                +{post.tags.length - 4} more
              </span>
            )}
          </div>
        )}

        {/* Media Preview */}
        {hasMedia && (
          <div className="px-4 pb-3">
            {post.media_urls[0].match(/\.(mp4|webm|mov)$/i) ? (
              <div className="rounded-lg overflow-hidden border border-border/30 aspect-[4/5] sm:aspect-[16/10] bg-surface-elevated">
                <video
                  src={resolveMediaUrl(post.media_urls[0])}
                  className="w-full h-full object-cover"
                  muted
                  playsInline
                />
              </div>
            ) : post.media_urls[0].match(/\.(mp3|wav|ogg)$/i) ? (
              <div className="rounded-lg overflow-hidden border border-border/30 bg-surface-elevated">
                <div className="h-20 flex items-center justify-center">
                  <MusicNote className="w-8 h-8 text-muted-foreground" />
                </div>
              </div>
            ) : (
              <div className="rounded-lg overflow-hidden border border-border/30 bg-surface-elevated">
                <img
                  src={resolveMediaUrl(post.media_urls[0])}
                  alt={post.title}
                  className="w-full h-auto max-h-[22rem] sm:max-h-[25rem] object-contain"
                />
              </div>
            )}
            {post.media_urls.length > 1 && (
              <p className="text-xs text-muted-foreground mt-1">
                +{post.media_urls.length - 1} more
              </p>
            )}
          </div>
        )}

        {/* Code Preview indicator */}
        {hasCode && !hasMedia && (
          <div className="mx-4 mb-3 p-3 rounded-lg bg-surface-elevated border border-border/40">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Code className="w-4 h-4 text-accent" />
              <span>Contains live HTML/CSS/JS preview</span>
            </div>
          </div>
        )}
      </Link>

      {/* Actions */}
      <div className="px-4 py-3 border-t border-border/40 flex items-center gap-4">
        <button
          onClick={handleLike}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors ${
            post.liked_by_user 
              ? "bg-danger/10 text-danger" 
              : "hover:bg-surface-elevated text-muted-foreground hover:text-foreground"
          }`}
          data-testid={`like-button-${post.id}`}
        >
          <Heart 
            weight={post.liked_by_user ? "fill" : "regular"} 
            className={`w-5 h-5 ${likeAnimating ? "heartbeat" : ""}`}
          />
          <span className="text-sm font-medium">{post.likes_count}</span>
        </button>
        <Link
          to={`/post/${post.id}`}
          className="flex items-center gap-2 px-3 py-1.5 rounded-full hover:bg-surface-elevated text-muted-foreground hover:text-foreground transition-colors"
          data-testid={`comment-button-${post.id}`}
        >
          <ChatCircle className="w-5 h-5" />
          <span className="text-sm font-medium">{post.comments_count}</span>
        </Link>
        <div className="flex items-center gap-1.5 ml-auto text-xs text-muted-foreground/60">
          <Eye className="w-3.5 h-3.5" />
          <span>{localViewsCount}</span>
        </div>
      </div>
    </article>
  );
}
